import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError, api } from "../lib/api";
import { btnGhost, btnPrimary, card, toast } from "./ui";

// Aba "Testar" do agente: o mesmo motor do playground do Valquiria, só a conversa.
// Sem execuções, logs, custo ou tools. Uma conversa sempre aberta; "Reiniciar"
// apaga e começa outra do zero. Resposta síncrona (até 300 s), sem streaming.

type Msg = { id: string; role: "user" | "assistant"; content: string; created_at: string };
const MAX = 2000;

export function TestChat({ agentId }: { agentId: string }) {
  const qc = useQueryClient();
  const key = ["test", agentId];
  const { data, isLoading } = useQuery<{ messages: Msg[] }>({ queryKey: key, queryFn: () => api.get(`/agents/${agentId}/test`) });
  const [text, setText] = useState("");
  const [pending, setPending] = useState<string | null>(null);  // balão otimista do cliente
  const listRef = useRef<HTMLDivElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: key });

  const send = useMutation({
    mutationFn: (content: string) => api.post<{ content: string | null; error: string | null }>(`/agents/${agentId}/test/messages`, { content }, { timeoutMs: 300_000 }),
    onMutate: (content) => { setPending(content); setText(""); },
    onSuccess: (r) => { if (r.error) toast(r.error); },
    // erro/timeout: o texto volta pra caixa; o refetch (abaixo) mostra o que o servidor de fato gravou
    onError: (e, content) => {
      setText((t) => t || content);
      toast(e instanceof ApiError ? e.detail ?? "Não foi possível enviar. Tente de novo."
        : "A resposta demorou demais. A conversa foi atualizada; tente de novo.");
    },
    onSettled: async () => { await refresh(); setPending(null); },  // otimista só some depois do refetch (sem duplicata)
  });
  const reset = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/test/reset`, {}),
    onSuccess: async () => { await refresh(); toast("Conversa reiniciada"); },
    onError: () => toast("Não foi possível reiniciar"),
  });

  const msgs = data?.messages ?? [];
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [msgs.length, pending, send.isPending]);

  const submit = () => { const t = text.trim(); if (t && !send.isPending) send.mutate(t); };

  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", height: "calc(100vh - 260px)", minHeight: 420 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--line)" }}>
        <span style={{ flex: 1, fontSize: 13, color: "var(--ink-muted)" }}>Converse como se fosse um cliente. Nada aqui vai pro WhatsApp.</span>
        <button style={{ ...btnGhost, height: 30, padding: "0 12px", fontSize: 12 }} disabled={reset.isPending || send.isPending}
          onClick={() => { if (confirm("Reiniciar a conversa? O histórico de teste é apagado.")) reset.mutate(); }}>Reiniciar</button>
      </div>

      <div ref={listRef} role="log" aria-live="polite" style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8, backgroundColor: "#EFEAE2", backgroundImage: WA_DOODLES }}>
        {isLoading && <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando…</div>}
        {!isLoading && msgs.length === 0 && !pending && (
          <div style={{ margin: "auto", textAlign: "center", color: "var(--ink-muted)", fontSize: 13, lineHeight: 1.6 }}>Mande uma mensagem pra testar o agente.<br />Ele responde com a configuração atual.</div>
        )}
        {msgs.map((m) => <Bubble key={m.id} role={m.role} text={m.content} />)}
        {pending && <Bubble role="user" text={pending} />}
        {send.isPending && <Bubble role="assistant" text="digitando…" dim />}
      </div>

      <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid var(--line)" }}>
        <textarea value={text} maxLength={MAX} rows={2} aria-label="Mensagem de teste" placeholder="Escreva como o cliente… (Enter envia, Shift+Enter quebra linha)"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); } }}
          style={{ flex: 1, padding: "8px 11px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", resize: "none", lineHeight: 1.45 }} />
        <button style={{ ...btnPrimary, alignSelf: "flex-end" }} disabled={send.isPending || !text.trim()} onClick={submit}>{send.isPending ? "Enviando…" : "Enviar"}</button>
      </div>
    </div>
  );
}

// Mesmo fundo do modal de conversas do painel (ConversationModal.WA_DOODLES).
const WA_DOODLES = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cg fill='none' stroke='%23DDD5C8' stroke-width='1.3' opacity='.6'%3E%3Ccircle cx='26' cy='32' r='7'/%3E%3Cpath d='M132 18l4 8 9 2-6 6 1 9-8-4-8 4 1-9-6-6 9-2z'/%3E%3Cpath d='M36 128q11-14 22 0t22 0'/%3E%3Ccircle cx='142' cy='128' r='5'/%3E%3Cpath d='M86 66l7 7m0-7l-7 7'/%3E%3Cpath d='M150 70q6-8 12 0'/%3E%3C/g%3E%3C/svg%3E")`;

function Bubble({ role, text, dim }: { role: "user" | "assistant"; text: string; dim?: boolean }) {
  const me = role === "user";
  // Cliente (user) à esquerda em branco; o agente à direita em verde, como o atendente vê no WhatsApp.
  return (
    <div role={dim ? "status" : undefined} style={{ alignSelf: me ? "flex-start" : "flex-end", maxWidth: "78%", padding: "8px 12px", borderRadius: 10, fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
      background: me ? "#fff" : "#D9FDD3", color: dim ? "#667781" : "#111B21", boxShadow: "0 1px .5px rgba(11,20,26,.13)" }}>
      {text}
    </div>
  );
}
